// Ruta: packages/api-client/src/client.ts
// Instancia ky configurada con interceptores de auth y error handling

import ky, { type KyInstance, type Options, HTTPError } from 'ky'

// ─── Token storage (in-memory via callback) ───────────────────────────────────
// The auth package injects these via setTokenCallbacks() to avoid circular deps

let _getAccessToken: (() => string | null) | null = null
let _onUnauthorized: (() => void) | null = null
let _refreshTokenFn: (() => Promise<string | null>) | null = null

export interface TokenCallbacks {
  getAccessToken: () => string | null
  onUnauthorized: () => void
  refreshToken: () => Promise<string | null>
}

export function setTokenCallbacks(callbacks: TokenCallbacks): void {
  _getAccessToken = callbacks.getAccessToken
  _onUnauthorized = callbacks.onUnauthorized
  _refreshTokenFn = callbacks.refreshToken
}

// ─── Track in-flight refresh to prevent race conditions ──────────────────────

let _isRefreshing = false
let _refreshPromise: Promise<string | null> | null = null

// ─── Base ky instance ─────────────────────────────────────────────────────────

const BFF_URL = (typeof window !== 'undefined'
  ? (import.meta as unknown as { env: Record<string, string> }).env?.VITE_BFF_URL
  : undefined) ?? 'http://localhost:3000'

export const apiClient: KyInstance = ky.create({
  prefixUrl: BFF_URL,
  timeout: 15_000,
  retry: {
    limit: 0, // We handle retry manually for 401
    methods: ['get', 'post', 'put', 'patch', 'delete'],
    statusCodes: [408, 429, 500, 502, 503, 504],
  },
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Accept-Language': 'es',
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = _getAccessToken?.()
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`)
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        if (response.status !== 401) return response

        // Don't attempt refresh for auth endpoints (login/refresh) — avoids loops
        if (request.url.includes('/auth/')) return response

        // Attempt token refresh
        if (_isRefreshing) {
          // Wait for the in-flight refresh
          const newToken = await _refreshPromise
          if (newToken) {
            const retryRequest = new Request(request, {
              headers: new Headers(request.headers),
            })
            retryRequest.headers.set('Authorization', `Bearer ${newToken}`)
            return ky(retryRequest, options as Options)
          }
        }

        if (_refreshTokenFn) {
          _isRefreshing = true
          _refreshPromise = _refreshTokenFn().finally(() => {
            _isRefreshing = false
            _refreshPromise = null
          })

          const newToken = await _refreshPromise
          if (newToken) {
            const retryRequest = new Request(request, {
              headers: new Headers(request.headers),
            })
            retryRequest.headers.set('Authorization', `Bearer ${newToken}`)
            return ky(retryRequest, options as Options)
          }
        }

        // Refresh failed — logout
        _onUnauthorized?.()
        return response
      },
    ],
    beforeError: [
      async (error) => {
        if (error instanceof HTTPError) {
          try {
            const body = await error.response.clone().json() as Record<string, unknown>
            const errData = body as { error?: { message?: string; code?: string } }
            if (errData.error?.message) {
              // Attach parsed message to error for use in UI
              ;(error as HTTPError & { bffMessage?: string }).bffMessage = errData.error.message
            }
          } catch {
            // ignore parse failure
          }
        }
        return error
      },
    ],
  },
})

// ─── Image URL helper ─────────────────────────────────────────────────────────

/**
 * Constructs a BFF-proxied URL for a drink image path returned by the upstream.
 * Example: getDrinkImageUrl(149, "png/7299/4815_21489.png")
 *   → "http://localhost:3000/api/v1/stores/149/drinks/image/png/7299/4815_21489.png"
 */
export function getDrinkImageUrl(storeId: number, imagePath: string): string {
  return `${BFF_URL}/api/v1/stores/${storeId}/drinks/image/${imagePath}`
}

// ─── Typed error extraction ───────────────────────────────────────────────────

export function getBFFErrorMessage(error: unknown): string {
  if (error instanceof HTTPError) {
    const extended = error as HTTPError & { bffMessage?: string }
    if (extended.bffMessage) return extended.bffMessage
    if (error.response.status === 401) return 'Sesión expirada. Por favor inicia sesión de nuevo.'
    if (error.response.status === 403) return 'No tienes permisos para realizar esta acción.'
    if (error.response.status === 404) return 'El recurso solicitado no existe.'
    if (error.response.status >= 500) return 'Error del servidor. Intenta más tarde.'
  }
  if (error instanceof Error) return error.message
  return 'Error desconocido'
}
